import { Code, ConnectError } from "@connectrpc/connect";
import { error, isHttpError, isRedirect } from "@sveltejs/kit";
import pluralize from "pluralize";
import { BadRequestSchema } from "#gen/google/rpc/error_details_pb.js";
import { Logger } from "#lib/logging.js";

export type Violations = Record<string, string>;
type HttpErrorStatus = 400 | 401 | 403 | 404 | 408 | 409 | 412 | 500 | 501 | 503;

export const RETRY_STATUS_CODES: ReadonlySet<Code> = new Set([Code.Canceled, Code.DeadlineExceeded, Code.Unavailable, Code.Unknown]);
const EDIT_FIXABLE_CODES: ReadonlySet<Code> = new Set([Code.InvalidArgument, Code.FailedPrecondition, Code.OutOfRange, Code.AlreadyExists]);

export class APIError extends Error {
	message: string;
	code: Code;
	statusCode: HttpErrorStatus;
	violations: Violations;

	constructor(message: string, code: Code = Code.Internal, violations: Violations = {}) {
		super();
		this.message = message;
		this.code = code;
		this.statusCode = statusFromCode(code);
		this.violations = violations;
	}
}

APIError.prototype.toString = function () {
	return `${this.message}`;
};

export class BulkError extends Error {
	failed: string[];
	errors: APIError[];

	constructor(failed: string[], errors: APIError[]) {
		super();
		this.failed = failed;
		this.errors = errors;
	}
}

BulkError.prototype.toString = function () {
	return `Failed to move ${this.errors.length} ${pluralize("item", this.errors.length)}. Please try again.`;
};

export function logError(err: unknown, route: string | null, params: Record<string, string>) {
	const fields = { route, params, err };
	Logger.error(`Internal error: ${err}`, fields);
	return;
}

export function handlePageLoadError(err: unknown, route: string | null, params: Record<string, string>) {
	const fields = { route, params, err };
	if (isRedirect(err)) {
		// Execute redirect
		return err;
	} else if (isHttpError(err)) {
		Logger.error(err.toString(), fields);
		return err;
	} else if (err instanceof APIError) {
		Logger.error(err.message, fields);
		return error(err.statusCode, err.message);
	} else if (err instanceof Error) {
		Logger.error(err.message, fields);
		return error(500, err.message);
	}
	Logger.error(`Internal error: ${err}`, fields);
	return error(500, "Internal error");
}

export function handleError(e: unknown): APIError | BulkError | undefined {
	if (e === undefined) {
		return undefined;
	}
	if (e instanceof APIError) {
		return e;
	}
	if (e instanceof BulkError) {
		return e;
	}
	if (e instanceof ConnectError) {
		if (e.code === Code.Canceled) {
			return undefined;
		}
		const violations: Violations = {};
		if (e.code === Code.InvalidArgument) {
			e.findDetails(BadRequestSchema).forEach(request => {
				request.fieldViolations.forEach(violation => {
					violations[violation.field] = violation.description;
				});
			});
		}
		return new APIError(e.rawMessage, e.code, violations);
	}
	return new APIError(`Unexpected error: ${e}`, Code.Internal);
}

export function isRetryableError(e: unknown): boolean {
	if (e === undefined) {
		return true;
	}
	return e instanceof APIError && RETRY_STATUS_CODES.has(e.code);
}

export function isFixableByEdit(e: unknown): boolean {
	return e instanceof APIError && EDIT_FIXABLE_CODES.has(e.code);
}

function statusFromCode(code: Code): HttpErrorStatus {
	switch (code) {
		case Code.InvalidArgument:
			return 400;
		case Code.Unauthenticated:
			return 401;
		case Code.PermissionDenied:
			return 403;
		case Code.NotFound:
			return 404;
		case Code.Aborted:
			return 409;
		case Code.FailedPrecondition:
			return 412;
		case Code.DeadlineExceeded:
			return 408;
		case Code.Unimplemented:
			return 501;
		case Code.Unavailable:
			return 503;
	}
	return 500;
}
